/**
 * Hero card used by payment-intent / inbound-payment detail pages.
 *
 * Large monospace amount + currency, status badge, and a meta line of
 * supplementary facts (tenant, direction, ids). The right-hand slot is
 * reserved for actions (refund, retry — coming later).
 */
import { CopyId } from "@/components/shell/CopyId";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatMinorUnits } from "@/lib/format";

export function DetailHero({
  backHref,
  backLabel,
  id,
  amount,
  currency,
  stateLabel,
  stateVariant,
  meta,
  actions,
}: {
  backHref: string;
  backLabel: string;
  id: string;
  amount: string;
  currency: string;
  stateLabel: string;
  stateVariant: BadgeProps["variant"];
  meta: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="mb-6 space-y-3">
      <Button asChild variant="ghost" size="sm" className="-ml-2 h-auto px-2 text-muted-foreground">
        <Link to={backHref}>
          <ArrowLeft />
          {backLabel}
        </Link>
      </Button>

      <div className="hero-surface rounded-lg p-5 shadow-md sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-3xl font-semibold tabular-nums sm:text-4xl">
                {formatMinorUnits(amount, currency)}
              </span>
              {/* bg-card keeps the badge's tint readable on the gradient. */}
              <span className="rounded-full bg-card">
                <Badge dot variant={stateVariant} className="capitalize">
                  {stateLabel.replace(/_/g, " ")}
                </Badge>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white">
              {meta}
            </div>
            <CopyId id={id} full className="max-w-full break-all text-left text-white hover:text-white focus-visible:ring-white" />
          </div>
          {actions ? <div className="hero-actions flex shrink-0 gap-2">{actions}</div> : null}
        </div>
      </div>
    </div>
  );
}
